import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Edit as EditIcon,
} from '@mui/icons-material'

import {
  PROFILE_LEVELS,
  PROFILE_SKILLS,
  languageProfileApi,
  type ProfileLevel,
  type StudentLanguageProfile,
} from '@/api/placement'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { PROBE_SKILLS, skillMeasurementLabel } from '@/utils/placementLabels'
import { formatDateTimeDMY } from '@/utils/date'

interface LanguageProfileCardProps {
  studentId: number
  /** Учитель правит уровни; ученику карточка показывается только на чтение. */
  canCorrect?: boolean
}

type CorrectionForm = {
  overall: ProfileLevel | ''
  skills: Record<string, ProfileLevel | ''>
  comment: string
}

/**
 * Языковой профиль ученика в карточке учителя.
 *
 * Показывает не одно число, а уровень по каждому навыку и источник оценки:
 * «B1 по самооценке» и «B1 по тесту» — разные основания, и учителю на первом
 * уроке важно знать, какое из них перед ним.
 *
 * Корректировка приоритетнее расчётной оценки: после неё повторный прогон
 * теста профиль уже не перезапишет. Поэтому правка требует явного действия и
 * пишется в историю вместе с комментарием.
 */
export function LanguageProfileCard({ studentId, canCorrect = false }: LanguageProfileCardProps) {
  const { t } = useTranslation()

  const [profile, setProfile] = useState<StudentLanguageProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<CorrectionForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)


  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setProfile(await languageProfileApi.show(studentId))
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setLoading(false)
    }
  }, [studentId, t])

  useEffect(() => {
    void load()
  }, [load])

  const formOf = (source: StudentLanguageProfile): CorrectionForm => ({
    overall: source.overall_level ?? '',
    skills: Object.fromEntries(
      PROFILE_SKILLS.map((skill) => [skill, source.skill_levels[skill] ?? '']),
    ) as Record<string, ProfileLevel | ''>,
    comment: '',
  })

  const openEditor = () => {
    if (profile === null) return

    setForm(formOf(profile))
    setDialogError(null)
    setEditing(true)
  }

  const closeEditor = () => {
    if (saving) return

    setEditing(false)
    setForm(null)
    setDialogError(null)
  }

  const save = async () => {
    if (profile === null || form === null) return

    setSaving(true)
    setDialogError(null)
    try {
      // Пустое значение означает «не трогать»: отправлять null значило бы
      // стереть уровень, который учитель просто не менял.
      const skillLevels: Record<string, ProfileLevel> = {}

      for (const skill of PROFILE_SKILLS) {
        const value = form.skills[skill]

        if (value !== '') {
          skillLevels[skill] = value
        }
      }

      setProfile(
        await languageProfileApi.correctLevels(studentId, {
          overall_level: form.overall === '' ? undefined : form.overall,
          skill_levels: skillLevels,
          comment: form.comment.trim() === '' ? null : form.comment.trim(),
        }),
      )

      setEditing(false)
      setForm(null)
    } catch (err) {
      setDialogError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setSaving(false)
    }
  }


  /**
   * Пробы письма и говорения.
   *
   * Автопроверки у них нет и в общую очередь проверки они не идут: тест ученик
   * проходит до того, как у него появился учитель. Прочитав пробу здесь,
   * учитель ставит уровень письма и говорения той же корректировкой.
   */

  /** Догрузить следующую страницу правок. */

  /** Источник оценки словами: «B1» без основания читателю ничего не говорит. */
  const sourceLabel = useMemo(() => {
    if (profile?.level_source == null) return null

    return t(`placement.sources.${profile.level_source}`)
  }, [profile?.level_source, t])

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {t('placement.profileTitle')}
          </Typography>

          {canCorrect && profile !== null && (
            <>
                <Tooltip title={t('placement.correctHint')}>
                <IconButton size="small" onClick={openEditor}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {loading && <Skeleton variant="rounded" height={120} />}

        {!loading && profile !== null && (
          <Stack spacing={2}>
            {!profile.has_level && (
              <Alert severity="info">{t('placement.notMeasuredYet')}</Alert>
            )}

            {profile.has_level && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5" fontWeight={700}>
                  {profile.overall_level}
                </Typography>
                {sourceLabel !== null && <Chip size="small" label={sourceLabel} />}
                {profile.assessed_at !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTimeDMY(profile.assessed_at)}
                  </Typography>
                )}
              </Box>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PROFILE_SKILLS.map((skill) => {
                const level = profile.skill_levels[skill]

                return (
                  <Chip
                    key={skill}
                    size="small"
                    variant={level ? 'filled' : 'outlined'}
                    color={level ? 'primary' : 'default'}
                    label={`${t(`placement.skills.${skill}`)}: ${
                      level
                      ?? skillMeasurementLabel(profile.skill_measurements?.[skill], t, {
                        isProbe: PROBE_SKILLS.includes(skill),
                      })
                    }`}
                  />
                )
              })}
            </Stack>

            {/* Последний тест разошёлся с профилем: после ручной правки
                результат прогона профиль уже не меняет, и увидеть его больше
                негде */}
            {profile.last_test !== null && (
              <Alert severity="info">
                <Typography variant="body2">
                  {t('placement.lastTestDiffers', {
                    date: formatDateTimeDMY(profile.last_test.completed_at),
                  })}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {Object.entries(profile.last_test.skill_levels).map(([skill, level]) => (
                    <Chip
                      key={skill}
                      size="small"
                      variant="outlined"
                      label={`${t(`placement.skills.${skill}`)}: ${level}`}
                    />
                  ))}
                </Stack>
              </Alert>
            )}

            {/* Самооценка отдельно от измеренного уровня: при расхождении
                берётся тест, но знать, каким ученик себя считает, полезно */}
            {profile.self_assessed_level !== null && (
              <Typography variant="body2" color="text.secondary">
                {t('placement.selfAssessedIs', { level: profile.self_assessed_level })}
              </Typography>
            )}


          </Stack>
        )}
      </CardContent>

      <Dialog
        open={editing}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return

          closeEditor()
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('placement.correctTitle')}</DialogTitle>
        <DialogContent dividers>
          {form !== null && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {dialogError && <Alert severity="error">{dialogError}</Alert>}

              <Alert severity="warning">{t('placement.correctWarning')}</Alert>

              <TextField
                select
                fullWidth
                size="small"
                label={t('placement.overallLevel')}
                value={form.overall}
                disabled={saving}
                onChange={(event) =>
                  setForm((current) =>
                    current === null ? current : { ...current, overall: event.target.value as ProfileLevel | '' },
                  )
                }
              >
                <MenuItem value="">{t('placement.leaveAsIs')}</MenuItem>
                {PROFILE_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </TextField>

              {PROFILE_SKILLS.map((skill) => (
                <TextField
                  key={skill}
                  select
                  fullWidth
                  size="small"
                  label={t(`placement.skills.${skill}`)}
                  value={form.skills[skill] ?? ''}
                  disabled={saving}
                  onChange={(event) =>
                    setForm((current) =>
                      current === null
                        ? current
                        : {
                            ...current,
                            skills: { ...current.skills, [skill]: event.target.value as ProfileLevel | '' },
                          },
                    )
                  }
                >
                  <MenuItem value="">{t('placement.leaveAsIs')}</MenuItem>
                  {PROFILE_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </TextField>
              ))}

              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={t('placement.correctComment')}
                helperText={t('placement.correctCommentHint')}
                value={form.comment}
                disabled={saving}
                onChange={(event) =>
                  setForm((current) => (current === null ? current : { ...current, comment: event.target.value }))
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" disabled={saving} onClick={() => void save()}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

    </Card>
  )
}
